import React, { useState, useEffect } from 'react';
import './App.css';
import Header from './components/Header';
import DataDisplay from './components/DataDisplay';
import ServerConfig from './components/ServerConfig';
import AdminManagement from './components/AdminManagement';
import Modal from './components/Modal';
import Login from './components/Login';
import { CrawlResponse } from './types';
import { checkHealth, crawlMatches, crawlMatchesPartial, queryMatchesFromDB, getInitialData, queryUpcomingMatches, crawlByDate } from './api';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { colors, typography, spacing } from './styles/design-system';

// design-system 토큰 기반 스타일 상수
const styles = {
  authLoadingScreen: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
  } as React.CSSProperties,

  authLoadingText: {
    color: colors.white,
    fontSize: typography.fontSize.xl,
  } as React.CSSProperties,

  retryButton: {
    marginLeft: spacing[4],
    padding: `${spacing[2]} ${spacing[5]}`,
    backgroundColor: colors.black,
    color: colors.white,
    border: 'none',
    cursor: 'pointer',
    fontSize: typography.fontSize.sm,
  } as React.CSSProperties,

  stateBox: {
    padding: `${spacing[10]} ${spacing[10]}`,
    textAlign: 'center' as const,
    backgroundColor: colors.white,
    marginTop: spacing[7],
    border: `1px solid ${colors.gray[200]}`,
  } as React.CSSProperties,

  stateBoxCompact: {
    padding: spacing[10],
    textAlign: 'center' as const,
    backgroundColor: colors.white,
    marginTop: spacing[7],
    border: `1px solid ${colors.gray[200]}`,
  } as React.CSSProperties,

  spinner: {
    width: '32px',
    height: '32px',
    border: `2px solid ${colors.gray[200]}`,
    borderTop: `2px solid ${colors.gray[800]}`,
    borderRadius: '50%',
    margin: `0 auto ${spacing[7]}`,
    animation: 'spin 1s linear infinite',
  } as React.CSSProperties,

  loadingTitle: {
    fontSize: typography.fontSize.md,
    color: colors.gray[800],
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing[3],
  } as React.CSSProperties,

  loadingSubtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.gray[500],
  } as React.CSSProperties,

  emptyText: {
    fontSize: typography.fontSize.base,
    color: colors.gray[600],
  } as React.CSSProperties,

  emptyTextSpaced: {
    fontSize: typography.fontSize.base,
    color: colors.gray[600],
    marginBottom: spacing[7],
  } as React.CSSProperties,

  refreshButton: {
    padding: `${spacing[2]} ${spacing[6]}`,
    backgroundColor: colors.black,
    color: colors.white,
    border: `1px solid ${colors.black}`,
    borderRadius: '0',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
    transition: 'all 0.2s',
    outline: 'none',
  } as React.CSSProperties,

  noPermissionBox: {
    padding: `48px ${spacing[10]}`,
    textAlign: 'center' as const,
    backgroundColor: colors.white,
    marginTop: spacing[7],
    border: `1px solid ${colors.gray[200]}`,
  } as React.CSSProperties,

  noPermissionText: {
    fontSize: typography.fontSize.md,
    color: colors.gray[600],
  } as React.CSSProperties,
};

function AppContent() {
  const { isAuthenticated, authLoading, currentUser, isAdmin, login, logout } = useAuth();

  const [serverStatus, setServerStatus] = useState<'online' | 'offline' | 'checking'>('checking');
  const [activeTab, setActiveTab] = useState<'matches' | 'config' | 'admin'>('matches');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true); // 초기 로딩 상태
  const [error, setError] = useState<string | null>(null);
  const [crawlData, setCrawlData] = useState<CrawlResponse | null>(null);
  const [aiProvider, setAiProvider] = useState<string>('');
  const [showCrawlModal, setShowCrawlModal] = useState(false);
  const [pendingCrawlParams, setPendingCrawlParams] = useState<{
    sport: string;
    league: string;
    date: string;
    partial: boolean;
    fromDate?: boolean;
  } | null>(null);
  const [initialData, setInitialData] = useState<{
    sports: Record<string, {
      name: string;
      leagues: Array<{value: string; label: string}>;
    }>;
    aiModels: Array<{value: string; label: string}>;
    defaultAIProvider: string;
  } | null>(null);

  // 세션 만료 시 crawlData 초기화
  useEffect(() => {
    if (!isAuthenticated && !authLoading) {
      setCrawlData(null);
    }
  }, [isAuthenticated, authLoading]);

  useEffect(() => {
    if (!isAuthenticated || authLoading) return;

    checkServerHealth();
    fetchInitialData();
    loadUpcomingMatches(); // 페이지 로드 시 예정 경기 조회
    const interval = setInterval(checkServerHealth, 300000);
    return () => clearInterval(interval);
  }, [isAuthenticated, authLoading]);

  // 로그인 성공 핸들러
  const handleLoginSuccess = (user: import('./api/auth').User) => {
    login(user);
    toast.success(`환영합니다, ${user.display_name}님!`);
  };

  // 로그아웃 핸들러
  const handleLogout = async () => {
    await logout();
    setCrawlData(null);
    toast.info('로그아웃 되었음.');
  };

  const checkServerHealth = async () => {
    try {
      await checkHealth();
      setServerStatus('online');
    } catch (error) {
      setServerStatus('offline');
    }
  };

  const fetchInitialData = async () => {
    try {
      const data = await getInitialData();
      setInitialData(data);
      // 기본 AI 모델 자동 설정 안함 - 사용자가 선택하도록
      // AI 모델 없이도 크롤링 가능 (예측 분석글 미생성)
    } catch (error) {
      console.error('초기 데이터 조회 실패:', error);
      // API 실패 시 기본 데이터 설정
      setInitialData({
        sports: {
          soccer: {
            name: "축구",
            leagues: [{ value: "", label: "전체" }]
          },
          baseball: {
            name: "야구",
            leagues: [{ value: "", label: "전체" }]
          },
          basketball: {
            name: "농구",
            leagues: [{ value: "", label: "전체" }]
          },
          volleyball: {
            name: "배구",
            leagues: [{ value: "", label: "전체" }]
          }
        },
        aiModels: [
          { value: "gemini", label: "Gemini" },
          { value: "claude", label: "Claude" },
          { value: "openai", label: "OpenAI" }
        ],
        defaultAIProvider: "gemini"
      });
    }
  };

  const loadUpcomingMatches = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('오늘 예정 경기 조회 중...');
      const data = await queryUpcomingMatches();
      console.log('예정 경기 조회 완료:', data);
      setCrawlData(data);
    } catch (error: any) {
      console.error('예정 경기 조회 실패:', error);
      setError(error.message || '예정 경기 조회 실패');
      // 초기 로딩이 아닐 때만 에러 토스트 표시
      if (!initialLoading) {
        toast.error('경기 정보를 불러오는데 실패했음.');
      }
    } finally {
      setLoading(false);
      setInitialLoading(false); // 초기 로딩 완료
    }
  };

  const handleCrawlConfirm = async () => {
    setShowCrawlModal(false);
    if (pendingCrawlParams) {
      const { sport, league, date, partial, fromDate } = pendingCrawlParams;
      await handleMatchQuery(sport, league, date, partial, false, fromDate);
      setPendingCrawlParams(null);
    }
  };


  const handleCrawlWithoutAI = async () => {
    setShowCrawlModal(false);
    if (pendingCrawlParams) {
      const { sport, league, date, partial } = pendingCrawlParams;
      setLoading(true);
      setError(null);
      try {
        // AI 모델 없이 크롤링 (예측 분석글 미생성)
        const data = partial
          ? await crawlMatchesPartial(sport, league || undefined, date || undefined)
          : await crawlMatches(sport, league || undefined, date || undefined);

        console.log('크롤링 결과 (AI 미사용):', data);
        setCrawlData(data);
        toast.success('크롤링이 완료되었음. (예측 분석글 없음)');
      } catch (err: any) {
        setError(err.message || '크롤링 실패');
      } finally {
        setLoading(false);
      }
      setPendingCrawlParams(null);
    }
  };

  const handleMatchQuery = async (sport: string, league: string, date: string, partial: boolean = false, isDB: boolean = false, fromDate: boolean = false, byDate: boolean = false) => {
    setLoading(true);
    setError(null);
    try {
      // 날짜별 전체 종목 크롤링
      if (byDate) {
        console.log('날짜별 전체 종목 크롤링:', date);
        const data = await crawlByDate(date, aiProvider || undefined);
        console.log('날짜별 크롤링 결과:', data);
        setCrawlData(data);
        toast.success(`${date} 날짜의 모든 종목 크롤링이 완료되었음. (총 ${data.count}개 경기)`);
        return;
      }

      // DB에서 조회
      if (isDB) {
        const data = await queryMatchesFromDB(sport, date, league || undefined);
        console.log('DB 조회 결과:', data);
        setCrawlData(data);
        toast.success(`DB에서 ${data.count}개의 경기를 조회했음.`);
        return;
      }

      // AI 모델 선택 여부 확인
      if (!aiProvider) {
        setLoading(false);
        setPendingCrawlParams({ sport, league, date, partial, fromDate });
        setShowCrawlModal(true);
        return;
      }

      // 크롤링 실행
      const data = partial
        ? await crawlMatchesPartial(sport, league || undefined, date || undefined, aiProvider || undefined)
        : await crawlMatches(sport, league || undefined, date || undefined, aiProvider || undefined);

      console.log('크롤링 결과:', data);
      setCrawlData(data);
      toast.success('크롤링이 완료되었음.');
    } catch (err: any) {
      setError(err.message || '경기 조회 실패');
      toast.error(err.message || '경기 조회 실패');
    } finally {
      setLoading(false);
    }
  };

  // 인증 로딩 중
  if (authLoading) {
    return (
      <div className="App" style={styles.authLoadingScreen}>
        <div style={styles.authLoadingText}>Loading...</div>
      </div>
    );
  }

  // 미인증 상태 - 로그인 화면 표시
  if (!isAuthenticated) {
    return (
      <>
        <Login onLoginSuccess={handleLoginSuccess} />
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </>
    );
  }

  return (
    <div className="App">
      <Header
        serverStatus={serverStatus}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      <div className="container">
        <div className="content">
          {activeTab === 'matches' ? (
            <>
              {error && !loading && (
                <div className="error-message">
                  {error}
                  <button onClick={loadUpcomingMatches} style={styles.retryButton}>
                    다시 시도
                  </button>
                </div>
              )}
              {initialLoading && loading && (
                <div style={styles.stateBox}>
                  <div style={styles.spinner} />
                  <div style={styles.loadingTitle}>경기 데이터를 조회 중임</div>
                  <div style={styles.loadingSubtitle}>잠시만 기다려.</div>
                  <style>{`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}</style>
                </div>
              )}
              {!initialLoading && loading && !crawlData && (
                <div style={styles.stateBoxCompact}>
                  <div style={styles.emptyText}>경기 정보를 불러오는 중...</div>
                </div>
              )}
              {!loading && !crawlData && !initialLoading && (
                <div style={styles.stateBoxCompact}>
                  <div style={styles.emptyTextSpaced}>
                    {error ? '경기 정보를 불러오지 못했음.' : '조회된 경기가 없음.'}
                  </div>
                  <button onClick={loadUpcomingMatches} style={styles.refreshButton}>
                    새로고침
                  </button>
                </div>
              )}
              {crawlData && (
                <DataDisplay
                  data={crawlData}
                  onRefresh={loadUpcomingMatches}
                  isLoading={loading}
                  currentUser={currentUser}
                />
              )}
            </>
          ) : activeTab === 'admin' ? (
            currentUser && isAdmin ? (
              <AdminManagement currentUser={currentUser} onLogout={handleLogout} />
            ) : (
              <div style={styles.noPermissionBox}>
                <div style={styles.noPermissionText}>접근 권한이 없음.</div>
              </div>
            )
          ) : activeTab === 'config' ? (
            currentUser && <ServerConfig currentUser={currentUser} />
          ) : null}
        </div>
      </div>

      <Modal
        isOpen={showCrawlModal}
        onClose={() => {
          if (!aiProvider) {
            // 선택하러 가기
            setShowCrawlModal(false);
            setPendingCrawlParams(null);
            toast.info('AI 모델을 선택해.');
          } else {
            handleCrawlConfirm();
          }
        }}
        onConfirm={() => {
          if (!aiProvider) {
            handleCrawlWithoutAI();
          } else {
            setShowCrawlModal(false);
            setPendingCrawlParams(null);
          }
        }}
        title={!aiProvider ? "AI 모델 미선택" : "데이터 없음"}
        message={!aiProvider
          ? "미선택시 예측 분석글이 작성되지 않음."
          : "DB에 저장된 경기 정보가 없음. 크롤링을 진행하시겠습니까?"}
        confirmText={!aiProvider ? "분석글 없이 크롤링" : "크롤링 진행"}
        cancelText={!aiProvider ? "선택하러 가기" : "취소"}
      />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
